/**
 * BYRÅSKANNINGEN, SAMMENLIGNET — forrige skanning mot den nye, per byrå (Atle, 28. september).
 *
 * Ren TypeScript uten I/O: importøren i API-et (`agency-scan-importer.ts`) leser basen, kaller
 * `compareAgencyScan` og skriver det som kommer ut. Alt som avgjør HVA som er en endring står her,
 * og derfor er det her enhetstestene står.
 *
 * ---------------------------------------------------------------------------------------------
 * ET BYTTE ER TO OBSERVASJONER, ET FRAVÆR ER ÉN.
 *
 * `record-agency-moves.mts` (7. september) sa det først: at en artist er borte fra byrå A kan
 * bety en redesignet nettside. At hen står hos B er en uavhengig observasjon som peker samme vei.
 * Derfor:
 *
 *   * Borte hos A og funnet hos B i SAMME skanning → én «Byttet»-rad A → B.
 *   * Borte hos A, ikke funnet noe sted → «Ut», artisten får «Uten kjent byrå». Dukker hen opp
 *     hos B i en SENERE skanning, gjøres den samme raden om til «Byttet» A → B (`completesOut`).
 *   * Funnet hos B mens A ikke ble lest (fila mangler, siden kunne ikke leses, eller sikringen slo
 *     inn) → ingen endring, men «Må sjekkes». Ett tegn er ikke nok til å flytte noen.
 *
 * ---------------------------------------------------------------------------------------------
 * SIKRINGEN: MER ENN 30 % FÆRRE ARTISTER ENN FORRIGE GANG → INGEN «UT» FOR DET BYRÅET.
 *
 * Et byrå som går fra 60 til 20 artister over natta har nesten alltid byttet nettside, ikke mistet
 * førti artister. Inn-radene og byttene INN til byrået står likevel — de er positive funn.
 *
 * DEN ANDRE SIKRINGEN (kjøring 01a17f3b, 27.10): MER ENN 30 % AV BYRÅETS ARTISTER USIKRE ELLER
 * BORTE → INGEN «UT» FOR DET BYRÅET. Antallet holdt seg likt hos Live Nation — 29 kort, men med
 * «Mer info» limt på hvert navn. Alle 29 ble usikre treff, ingen ble funnet, og alle 29 gikk ut.
 * En registrert artist som er kandidat i et usikkert treff hos byrået regnes dessuten alltid som
 * funnet der: et usikkert navn kan aldri bli et uttak, bare «Må sjekkes».
 *
 * ---------------------------------------------------------------------------------------------
 * NAVNEMATCHING: likt etter normalisering er samme artist. Nesten likt slås ALDRI sammen — det
 * havner under «Må sjekkes», og det lages heller ingen ny artist (det ville vært duplikatet).
 */

export const AGENCY_MOVE_KINDS = ['in', 'out', 'switched'] as const;
export type AgencyMoveKind = (typeof AGENCY_MOVE_KINDS)[number];

/** Kildene en endring kan ha: skanningen, eller byrået selv (et byrå som er i Tvitvi). */
export const AGENCY_MOVE_SOURCES = ['scan', 'agency_self'] as const;
export type AgencyMoveSource = (typeof AGENCY_MOVE_SOURCES)[number];

export const AGENCY_REVIEW_KINDS = [
  'uncertain_match',
  'count_drop',
  'multiple_agencies',
  'found_elsewhere',
  'unreadable',
  /** Artisten står på en liste, men agenten hører til et byrå registeret ikke har (Luger, FKP). */
  'unknown_agency',
] as const;
export type AgencyReviewKind = (typeof AGENCY_REVIEW_KINDS)[number];

/** «Mer enn 30 % færre artister hos et byrå enn forrige gang.» */
export const AGENCY_SCAN_GUARD_DROP = 0.3;

/** «Mer enn 30 % av byråets artister er usikre treff eller borte i denne skanningen.» */
export const AGENCY_SCAN_GUARD_UNSURE = 0.3;

/**
 * …og minst så mange. Uten gulvet holdt sikringen igjen et lite byrå som mister én av to artister — et
 * vanlig, ekte uttak — mens det den er laget for, er at et helt byrå forsvinner på en lesefeil (27.10).
 */
export const AGENCY_SCAN_GUARD_UNSURE_MIN = 3;

/**
 * Den første skanningens måned. Atle: «Alt som finnes i første skanning får 2026-09.» Vi vet ikke
 * når det egentlig skjedde, bare når vi først så det.
 */
export const FIRST_SCAN_MONTH = '2026-09';

export const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

// ------------------------------------------------------------------------------ navnene

const LETTER_FOLDS: Readonly<Record<string, string>> = {
  ø: 'o',
  æ: 'ae',
  ß: 'ss',
  đ: 'd',
  ł: 'l',
  œ: 'oe',
  þ: 'th',
};

/**
 * LANDSKODEN I NAVNET (223.7): «Wintergatan (SE)», «Mark Olson (US)», «MØ [DK]». To eller tre STORE
 * bokstaver i parentes eller hakeparentes er geografi, ikke navn. Små bokstaver står: «(live)» og
 * «(feat)» er ikke koder, og en kode skrives med store. Bare sammenligningen ser bort fra den —
 * visningsnavnet beholder den.
 */
const COUNTRY_CODE = /\s*(?:\(\p{Lu}{2,3}\)|\[\p{Lu}{2,3}\])/gu;

/**
 * Et navn slik to skrivemåter av den samme artisten blir like.
 *
 * Landskoden bort (se `COUNTRY_CODE`), små bokstaver, diakritiske tegn bort (ø og æ er egne
 * bokstaver og brettes for hånd), «&», «+», «and» og «og» blir det samme ordet, og en ledende «The»
 * faller bort. Tegnsetting blir mellomrom.
 */
export function normalizeArtistName(name: string): string {
  return normalizeName(name, true);
}

/**
 * Den samme normaliseringen for en AGENT, men uten å fjerne parentesen: «Håkon (ATL)» er lagret
 * som `hakon atl` i `booking_agency_agents.normalized_name` (unik per byrå), og en ny nøkkel ville
 * gjort neste skanning til en ny rad for den samme agenten.
 */
export function normalizeAgentName(name: string): string {
  return normalizeName(name, false);
}

function normalizeName(name: string, dropCountryCode: boolean): string {
  const bare = dropCountryCode ? name.replace(COUNTRY_CODE, ' ') : name;
  const lowered = bare.normalize('NFKD').toLowerCase();
  let folded = '';
  for (const char of lowered) folded += LETTER_FOLDS[char] ?? char;
  const words = folded
    .replace(/\p{M}+/gu, '')
    .replace(/[&+]/g, ' og ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(' ')
    .filter((word) => word !== '')
    .map((word) => (word === 'and' ? 'og' : word));
  if (words.length > 1 && words[0] === 'the') words.shift();
  return words.join(' ');
}

/** Normalisert og uten mellomrom: «2AM-DM» og «2AMDM» er det samme bandet. */
export function compactArtistName(name: string): string {
  return normalizeArtistName(name).replace(/ /g, '');
}

function levenshtein(a: string, b: string, limit: number): number {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    let best = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const value = Math.min(
        (previous[j] ?? 0) + 1,
        (current[j - 1] ?? 0) + 1,
        (previous[j - 1] ?? 0) + cost,
      );
      current.push(value);
      if (value < best) best = value;
    }
    if (best > limit) return limit + 1;
    previous = current;
  }
  return previous[b.length] ?? limit + 1;
}

export type NameMatch = 'same' | 'uncertain' | 'different';

/**
 * Er to agentnavn det samme mennesket? Rausere enn artistnavn, fordi et falskt agentbytte bare er
 * støy i historikken: «Daniel» og «Daniel Gundersen» er én agent, og det er «jon@timeout.no» og
 * «Jon Even Moe» også — siden oppgir adressen, Greenroom oppga navnet.
 */
export function isSameAgent(a: string, b: string): boolean {
  if (compareArtistNames(a, b) !== 'different') return true;
  const firstName = (value: string): string => normalizeArtistName(value).split(' ')[0] ?? '';
  const local = (value: string): string | null =>
    value.includes('@') ? normalizeArtistName(value.slice(0, value.indexOf('@'))) : null;
  const left = local(a);
  const right = local(b);
  if (left !== null && right === null) return left === firstName(b);
  if (right !== null && left === null) return right === firstName(a);
  const [shortName, longName] = a.length <= b.length ? [a, b] : [b, a];
  const words = normalizeArtistName(shortName).split(' ');
  return words.length === 1 && words[0] === firstName(longName);
}

/**
 * Er to navn den samme artisten?
 *
 * `uncertain` er et svar, ikke en feil: én eller to bokstaver fra hverandre («Sigrid» / «Sigrit»),
 * eller det ene navnet er det andre med noe til («Aurora» / «Aurora Aksnes»). Korte navn får ikke
 * slingringsmonn — «Ida» og «Idun» er to mennesker.
 */
export function compareArtistNames(a: string, b: string): NameMatch {
  const left = normalizeArtistName(a);
  const right = normalizeArtistName(b);
  if (left === '' || right === '') return 'different';
  const compactLeft = left.replace(/ /g, '');
  const compactRight = right.replace(/ /g, '');
  if (left === right || compactLeft === compactRight) return 'same';

  const shortest = Math.min(compactLeft.length, compactRight.length);
  if (shortest >= 5) {
    const limit = shortest >= 9 ? 2 : 1;
    if (levenshtein(compactLeft, compactRight, limit) <= limit) return 'uncertain';
  }

  const leftWords = left.split(' ');
  const rightWords = right.split(' ');
  const [short, long] =
    leftWords.length <= rightWords.length ? [leftWords, rightWords] : [rightWords, leftWords];
  if (short.length < long.length && short.join('').length >= 4) {
    const head = long.slice(0, short.length).join(' ');
    const tail = long.slice(long.length - short.length).join(' ');
    const wanted = short.join(' ');
    if (head === wanted || tail === wanted) return 'uncertain';
  }
  return 'different';
}

// ------------------------------------------------------------------------------ modellen

/** En artist i husets register slik sammenligningen trenger den. */
export interface ScanRegisterArtist {
  id: string;
  name: string;
  /** Byrået registeret sier i dag. Null er «Uten kjent byrå». */
  agencyId: string | null;
  /** Agentens navn i dag, for å se et agentbytte. */
  agentName: string | null;
  /**
   * En «Ut»-rad som venter på å bli et bytte: artisten falt ut av `fromAgencyId` i en tidligere
   * skanning og er ikke funnet igjen. Satt bare når `agencyId` er null.
   */
  openOut: { changeId: string; fromAgencyId: string } | null;
  /**
   * Huset har satt byrået selv i årgangen det jobber med (225.4). Skanningen flytter henne ikke —
   * ingen Inn, Ut eller Byttet — men et funn hos et annet byrå kommer ut som `lockedFindings`, så
   * det kan vises som et varsel. Fra neste årgang er feltet ikke låst lenger.
   */
  agencyLocked?: boolean;
}

export interface ScannedArtist {
  name: string;
  agent: string | null;
  image: string | null;
  genre: string | null;
  base: string | null;
  source: string | null;
  /**
   * Leseren tviler på at dette er en artist: lenka følger ikke artistlenkenes mønster på siden
   * (en meny ved siden av lista). Et kjent navn teller som funnet; et ukjent blir «Må sjekkes»,
   * aldri en ny artist.
   */
  uncertain?: boolean;
}

export interface ScannedAgency {
  agencyId: string;
  status: 'ok' | 'unreadable';
  artists: readonly ScannedArtist[];
  /**
   * Artister som står på byråets side, men som bookes av et byrå vi ikke kjenner (se
   * `groupScanFiles`). De flytter ingen og lager ingen, men de er ikke borte: en artist her får
   * aldri en «Ut»-rad fra dette byrået.
   */
  held?: readonly (ScannedArtist & { domain: string | null })[];
  /**
   * Byrået ble bare sett gjennom en ANNENS liste (Act Agency på Live Nations felles side), ikke på
   * sin egen. Funnene teller som funn, men lista er ikke hele stallen: ingen «Ut», ingen sikring.
   */
  partial?: boolean;
  /** Nettsiden eller siden artistlista sto på, som bevis for byråets egne rader. */
  sourceUrl: string | null;
}

/** Det forrige vellykkede funnet for et byrå. */
export interface PreviousAgencyFindings {
  count: number;
  artistIds: readonly string[];
}

export interface CompareAgencyScanInput {
  register: readonly ScanRegisterArtist[];
  agencies: readonly ScannedAgency[];
  previous: ReadonlyMap<string, PreviousAgencyFindings>;
  /** Byråer som selv er i Tvitvi. De skannes ikke, og artistene deres flyttes aldri herfra. */
  tvitviAgencyIds: ReadonlySet<string>;
  /**
   * Byråer som skannes for FØRSTE gang (223.2): agenten registeret har kommer fra Greenroom, og en
   * uenighet med byråsiden er gammel data, ikke et bytte. Agenten overskrives, men føres ikke.
   */
  firstScanAgencyIds?: ReadonlySet<string>;
  guardDrop?: number;
  guardUnsure?: number;
}

export type FindingMatch =
  | { kind: 'existing'; artistId: string }
  | { kind: 'new' }
  | { kind: 'review' }
  | { kind: 'duplicate' };

export interface ScanFinding {
  agencyId: string;
  artist: ScannedArtist;
  normalizedName: string;
  match: FindingMatch;
}

/** Hvem en endring gjelder: en artist som finnes, eller et funn som blir en ny artist. */
export type ScanArtistRef = { artistId: string } | { findingIndex: number };

export interface ScanChange {
  kind: AgencyMoveKind;
  artist: ScanArtistRef;
  fromAgencyId: string | null;
  toAgencyId: string | null;
  evidenceUrl: string | null;
  /** Satt når en tidligere «Ut»-rad gjøres om til dette byttet. */
  completesOut: string | null;
}

export interface ScanAgentChange {
  artistId: string;
  agencyId: string;
  fromAgent: string;
  toAgent: string;
  evidenceUrl: string | null;
}

/** Et funn skanningen ikke fikk bruke, fordi huset har låst byrået i årgangen (225.4). */
export interface ScanLockedFinding {
  artistId: string;
  /** Byrået skanningen fant henne hos. */
  foundAgencyId: string;
  evidenceUrl: string | null;
}

export interface ScanReviewItem {
  kind: AgencyReviewKind;
  agencyId: string | null;
  artistName: string | null;
  candidateArtistIds: string[];
  evidenceUrl: string | null;
  detail: Record<string, number | string | null>;
}

export interface CompareAgencyScanResult {
  findings: ScanFinding[];
  changes: ScanChange[];
  agentChanges: ScanAgentChange[];
  review: ScanReviewItem[];
  lockedFindings: ScanLockedFinding[];
  /** Byråer der sikringen slo inn — ingen «Ut»-rader for dem i denne skanningen. */
  guardedAgencyIds: string[];
}

interface Located {
  agencyId: string;
  findingIndex: number;
}

/**
 * Sammenligner den nye skanningen mot registeret og forrige skanning.
 *
 * Rekkefølgen er deterministisk (byråene i den rekkefølgen de kommer, artistene i sin), så to
 * kjøringer på de samme filene gir de samme radene.
 */
export function compareAgencyScan(input: CompareAgencyScanInput): CompareAgencyScanResult {
  const guardDrop = input.guardDrop ?? AGENCY_SCAN_GUARD_DROP;
  const guardUnsure = input.guardUnsure ?? AGENCY_SCAN_GUARD_UNSURE;
  const findings: ScanFinding[] = [];
  const changes: ScanChange[] = [];
  const agentChanges: ScanAgentChange[] = [];
  const review: ScanReviewItem[] = [];
  const lockedFindings: ScanLockedFinding[] = [];
  const guarded: string[] = [];
  const firstScan = input.firstScanAgencyIds ?? new Set<string>();

  const byId = new Map(input.register.map((artist) => [artist.id, artist]));
  const byNormalized = new Map<string, ScanRegisterArtist[]>();
  for (const artist of input.register) {
    const key = compactArtistName(artist.name);
    if (key === '') continue;
    const bucket = byNormalized.get(key) ?? [];
    bucket.push(artist);
    byNormalized.set(key, bucket);
  }

  const readable = new Set<string>();
  const foundAt = new Map<string, Located[]>();
  const newByName = new Map<string, Located[]>();
  /** Artister som står på et byrås side men bookes av et ukjent byrå: aldri «Ut» derfra. */
  const heldAt = new Map<string, Set<string>>();
  /** Registerartister som er kandidat i et usikkert treff hos et byrå: regnet som funnet der. */
  const candidateAt = new Map<string, Set<string>>();
  const uncertainAt = (agencyId: string, candidates: readonly string[]): void => {
    const kept = candidateAt.get(agencyId) ?? new Set<string>();
    for (const id of candidates) kept.add(id);
    candidateAt.set(agencyId, kept);
  };

  for (const agency of input.agencies) {
    if (input.tvitviAgencyIds.has(agency.agencyId)) continue;
    if (agency.status !== 'ok') {
      review.push({
        kind: 'unreadable',
        agencyId: agency.agencyId,
        artistName: null,
        candidateArtistIds: [],
        evidenceUrl: agency.sourceUrl,
        detail: {},
      });
      continue;
    }
    if (agency.partial !== true) readable.add(agency.agencyId);

    const seen = new Set<string>();
    let count = 0;
    for (const artist of agency.artists) {
      const compact = compactArtistName(artist.name);
      const normalizedName = normalizeArtistName(artist.name);
      const index = findings.length;
      if (compact === '' || seen.has(compact)) {
        findings.push({
          agencyId: agency.agencyId,
          artist,
          normalizedName,
          match: { kind: 'duplicate' },
        });
        continue;
      }
      seen.add(compact);
      count += 1;

      const exact = byNormalized.get(compact) ?? [];
      let match: FindingMatch;
      if (exact.length === 1 && exact[0] !== undefined) {
        match = { kind: 'existing', artistId: exact[0].id };
      } else if (exact.length > 1) {
        const here = exact.filter((one) => one.agencyId === agency.agencyId);
        if (here.length === 1 && here[0] !== undefined) {
          match = { kind: 'existing', artistId: here[0].id };
        } else {
          match = { kind: 'review' };
          uncertainAt(
            agency.agencyId,
            exact.map((one) => one.id),
          );
          review.push({
            kind: 'uncertain_match',
            agencyId: agency.agencyId,
            artistName: artist.name,
            candidateArtistIds: exact.map((one) => one.id),
            evidenceUrl: artist.source ?? agency.sourceUrl,
            detail: {},
          });
        }
      } else {
        const near = input.register.filter(
          (one) => compareArtistNames(one.name, artist.name) === 'uncertain',
        );
        if (near.length > 0) {
          match = { kind: 'review' };
          uncertainAt(
            agency.agencyId,
            near.map((one) => one.id),
          );
          review.push({
            kind: 'uncertain_match',
            agencyId: agency.agencyId,
            artistName: artist.name,
            candidateArtistIds: near.map((one) => one.id),
            evidenceUrl: artist.source ?? agency.sourceUrl,
            detail: {},
          });
        } else if (artist.uncertain === true) {
          /* Leseren tvilte (en lenke utenfor artistmønsteret): aldri en ny artist på det. */
          match = { kind: 'review' };
          review.push({
            kind: 'uncertain_match',
            agencyId: agency.agencyId,
            artistName: artist.name,
            candidateArtistIds: [],
            evidenceUrl: artist.source ?? agency.sourceUrl,
            detail: { reason: 'outside_link_pattern' },
          });
        } else {
          match = { kind: 'new' };
          const bucket = newByName.get(compact) ?? [];
          bucket.push({ agencyId: agency.agencyId, findingIndex: index });
          newByName.set(compact, bucket);
        }
      }
      findings.push({ agencyId: agency.agencyId, artist, normalizedName, match });
      if (match.kind === 'existing') {
        const bucket = foundAt.get(match.artistId) ?? [];
        bucket.push({ agencyId: agency.agencyId, findingIndex: index });
        foundAt.set(match.artistId, bucket);
      }
    }

    for (const artist of agency.held ?? []) {
      const compact = compactArtistName(artist.name);
      if (compact === '' || seen.has(compact)) continue;
      seen.add(compact);
      count += 1;
      const exact = byNormalized.get(compact) ?? [];
      const kept = heldAt.get(agency.agencyId) ?? new Set<string>();
      for (const one of exact) kept.add(one.id);
      heldAt.set(agency.agencyId, kept);
      review.push({
        kind: 'unknown_agency',
        agencyId: agency.agencyId,
        artistName: artist.name,
        candidateArtistIds: exact.map((one) => one.id),
        evidenceUrl: artist.source ?? agency.sourceUrl,
        detail: { domain: artist.domain },
      });
    }

    const before = agency.partial === true ? undefined : input.previous.get(agency.agencyId);
    if (
      before !== undefined &&
      before.count > 0 &&
      (before.count - count) / before.count > guardDrop
    ) {
      guarded.push(agency.agencyId);
      review.push({
        kind: 'count_drop',
        agencyId: agency.agencyId,
        artistName: null,
        candidateArtistIds: [],
        evidenceUrl: agency.sourceUrl,
        detail: { previous: before.count, current: count },
      });
    }
  }

  /*
   * Den andre sikringen (se headeren). Grunnlaget er artistene forrige skanning fant hos byrået og
   * som fortsatt står der; funnet hos et annet byrå er et bytte og teller som funnet.
   */
  for (const agency of input.agencies) {
    const agencyId = agency.agencyId;
    if (!readable.has(agencyId) || guarded.includes(agencyId)) continue;
    const before = input.previous.get(agencyId);
    if (before === undefined) continue;
    let total = 0;
    let unsure = 0;
    for (const artistId of new Set(before.artistIds)) {
      const artist = byId.get(artistId);
      if (artist === undefined || artist.agencyId !== agencyId || artist.agencyLocked === true)
        continue;
      total += 1;
      if (foundAt.has(artistId) || heldAt.get(agencyId)?.has(artistId) === true) continue;
      unsure += 1;
    }
    if (total === 0 || unsure < AGENCY_SCAN_GUARD_UNSURE_MIN || unsure / total <= guardUnsure)
      continue;
    guarded.push(agencyId);
    review.push({
      kind: 'count_drop',
      agencyId,
      artistName: null,
      candidateArtistIds: [],
      evidenceUrl: agency.sourceUrl,
      /* `previous`/`current` er det «Må sjekkes» viser: kjente artister, og hvor mange som ble sikkert gjenkjent. */
      detail: {
        previous: total,
        current: total - unsure,
        reason: 'uncertain_or_missing',
        uncertain: [...(candidateAt.get(agencyId) ?? [])].filter(
          (id) => byId.get(id)?.agencyId === agencyId && !foundAt.has(id),
        ).length,
      },
    });
  }
  const guardedSet = new Set(guarded);
  const evidenceOf = (at: Located): string | null => {
    const finding = findings[at.findingIndex];
    return finding?.artist.source ?? null;
  };

  // ---- artister som finnes fra før og ble funnet et sted
  const moved = new Set<string>();
  for (const [artistId, places] of foundAt) {
    const artist = byId.get(artistId);
    if (artist === undefined) continue;
    const home = places.find((place) => place.agencyId === artist.agencyId);

    if (home !== undefined) {
      const finding = findings[home.findingIndex];
      const agent = finding?.artist.agent ?? null;
      if (
        !firstScan.has(home.agencyId) &&
        artist.agentName !== null &&
        agent !== null &&
        !isSameAgent(artist.agentName, agent)
      ) {
        agentChanges.push({
          artistId,
          agencyId: home.agencyId,
          fromAgent: artist.agentName,
          toAgent: agent,
          evidenceUrl: evidenceOf(home),
        });
      }
      if (places.length > 1) {
        review.push({
          kind: 'multiple_agencies',
          agencyId: places.find((place) => place !== home)?.agencyId ?? null,
          artistName: artist.name,
          candidateArtistIds: [artistId],
          evidenceUrl: evidenceOf(places.find((place) => place !== home) ?? home),
          detail: { agencies: places.length },
        });
      }
      continue;
    }

    if (places.length > 1) {
      review.push({
        kind: 'multiple_agencies',
        agencyId: null,
        artistName: artist.name,
        candidateArtistIds: [artistId],
        evidenceUrl: evidenceOf(places[0] as Located),
        detail: { agencies: places.length },
      });
      continue;
    }
    const place = places[0];
    if (place === undefined) continue;

    if (artist.agencyLocked === true) {
      lockedFindings.push({
        artistId,
        foundAgencyId: place.agencyId,
        evidenceUrl: evidenceOf(place),
      });
      continue;
    }

    if (artist.agencyId === null) {
      /* Tilbake hos det samme byrået er en ny «Inn», ikke et bytte fra A til A. */
      const out =
        artist.openOut !== null && artist.openOut.fromAgencyId !== place.agencyId
          ? artist.openOut
          : null;
      changes.push({
        kind: out === null ? 'in' : 'switched',
        artist: { artistId },
        fromAgencyId: out?.fromAgencyId ?? null,
        toAgencyId: place.agencyId,
        evidenceUrl: evidenceOf(place),
        completesOut: out?.changeId ?? null,
      });
      moved.add(artistId);
      continue;
    }

    /* Et usikkert treff hjemme betyr at hun trolig står der fortsatt: ikke et bytte. */
    const canLeave =
      readable.has(artist.agencyId) &&
      !guardedSet.has(artist.agencyId) &&
      !input.tvitviAgencyIds.has(artist.agencyId) &&
      candidateAt.get(artist.agencyId)?.has(artistId) !== true;
    if (canLeave) {
      changes.push({
        kind: 'switched',
        artist: { artistId },
        fromAgencyId: artist.agencyId,
        toAgencyId: place.agencyId,
        evidenceUrl: evidenceOf(place),
        completesOut: null,
      });
      moved.add(artistId);
    } else {
      review.push({
        kind: 'found_elsewhere',
        agencyId: place.agencyId,
        artistName: artist.name,
        candidateArtistIds: [artistId],
        evidenceUrl: evidenceOf(place),
        detail: {},
      });
    }
  }

  // ---- nye artister: ett byrå gir en ny registerartist, to byråer gir «Må sjekkes»
  for (const places of newByName.values()) {
    if (places.length > 1) {
      for (const place of places) {
        const finding = findings[place.findingIndex];
        if (finding !== undefined) finding.match = { kind: 'review' };
      }
      const first = findings[places[0]?.findingIndex ?? -1];
      review.push({
        kind: 'multiple_agencies',
        agencyId: null,
        artistName: first?.artist.name ?? null,
        candidateArtistIds: [],
        evidenceUrl: first?.artist.source ?? null,
        detail: { agencies: places.length },
      });
      continue;
    }
    const place = places[0];
    if (place === undefined) continue;
    changes.push({
      kind: 'in',
      artist: { findingIndex: place.findingIndex },
      fromAgencyId: null,
      toAgencyId: place.agencyId,
      evidenceUrl: evidenceOf(place),
      completesOut: null,
    });
  }

  // ---- «Ut»: sto hos byrået forrige gang, er ikke der nå, og er ikke funnet noe annet sted
  for (const agencyId of readable) {
    if (guardedSet.has(agencyId)) continue;
    const before = input.previous.get(agencyId);
    if (before === undefined) continue;
    for (const artistId of before.artistIds) {
      const artist = byId.get(artistId);
      if (artist === undefined || artist.agencyId !== agencyId || moved.has(artistId)) continue;
      if (artist.agencyLocked === true) continue;
      if (foundAt.has(artistId) || heldAt.get(agencyId)?.has(artistId) === true) continue;
      if (candidateAt.get(agencyId)?.has(artistId) === true) continue;
      changes.push({
        kind: 'out',
        artist: { artistId },
        fromAgencyId: agencyId,
        toAgencyId: null,
        evidenceUrl: input.agencies.find((one) => one.agencyId === agencyId)?.sourceUrl ?? null,
        completesOut: null,
      });
    }
  }

  return { findings, changes, agentChanges, review, lockedFindings, guardedAgencyIds: guarded };
}

/** «2026-09-28T16:03:47Z» → «2026-09» i Oslo-tid. Aldri en dato — Atle vil ha måneden. */
export function scanMonthOf(scannedAt: string): string {
  const date = new Date(scannedAt);
  if (Number.isNaN(date.getTime())) throw new Error(`tvitvi: "${scannedAt}" is not a timestamp.`);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Oslo',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  return `${year}-${month}`;
}

// ------------------------------------------------------------------------------ filene

/** Én skanningsfil slik research-agenten skriver den (`docs/qa/byraskanning/<slug>.json`). */
export interface AgencyScanFile {
  agency: string;
  website?: string | null;
  scannedAt: string;
  sources?: readonly string[];
  generalContact?: { email?: string | null; phone?: string | null; source?: string | null } | null;
  agents?: readonly {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    source?: string | null;
    company?: string | null;
  }[];
  artists?: readonly {
    name: string;
    agent?: string | null;
    agentEmail?: string | null;
    image?: string | null;
    genre?: string | null;
    base?: string | null;
    source?: string | null;
    /** Se `ScannedArtist.uncertain`. */
    uncertain?: boolean | null;
  }[];
  status?: string;
  reason?: string | null;
}

export interface ScanContact {
  name: string;
  phone: string | null;
  email: string | null;
  source: string | null;
}

export interface GroupedScanAgency {
  /** `normalizeAgencyName` av navnet — nøkkelen byrået kobles til registeret på. */
  key: string;
  name: string;
  website: string | null;
  status: 'ok' | 'unreadable';
  reason: string | null;
  sourceUrl: string | null;
  sources: string[];
  /** Fila (eller filene) byrået ble lest fra. */
  files: string[];
  /**
   * True når byrået har sin egen fil. Et byrå som bare finnes gjennom en felles liste (Act Agency
   * gjennom Live Nations) er ikke lest helt, og får derfor ingen «Ut»-rader.
   */
  ownFile: boolean;
  generalContact: ScanContact | null;
  agents: ScanContact[];
  artists: ScannedArtist[];
  /**
   * Tom fra `groupScanFiles` siden 29.09: et ukjent domene regnes som byrået siden står på (se der).
   * Feltet står fordi sammenligningen fortsatt kan få holdte artister fra en annen kilde.
   */
  held: (ScannedArtist & { domain: string | null })[];
}

/** «Live Nation (Norge)» og «Live Nation» er det samme byrået. Parentesen er geografi, ikke navn. */
export function normalizeAgencyName(name: string): string {
  return normalizeArtistName(name.replace(/\([^)]*\)/g, ' '));
}

/**
 * Domenet en adresse eller nettside hører til: `bookartist.livenation.no` → `livenation.no`.
 * To siste ledd holder for .no, .as, .com og .se, som er det byråene bruker.
 */
export function registrableDomain(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const text = value.trim().toLowerCase();
  if (text === '') return null;
  let host: string;
  const at = text.lastIndexOf('@');
  if (at >= 0) host = text.slice(at + 1);
  else {
    try {
      host = new URL(text.includes('://') ? text : `https://${text}`).hostname;
    } catch {
      return null;
    }
  }
  const parts = host.split('.').filter((part) => part !== '');
  if (parts.length < 2) return null;
  return parts.slice(-2).join('.');
}

/** Den første adressen i «kasper@livenation.no eller vemund@livenation.no». */
export function firstEmail(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const found = /[^\s,;<>()]+@[^\s,;<>()]+\.[a-z]{2,}/i.exec(value);
  return found === null ? null : found[0].toLowerCase();
}

const clean = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const text = value.trim();
  return text === '' ? null : text;
};

/**
 * Fordeler filene på BYRÅ, ikke på fil.
 *
 * ---------------------------------------------------------------------------------------------
 * EN FIL ER IKKE ET BYRÅ. `bookartist.livenation.no` er én felles liste for Live Nation, ACT,
 * Luger og TimeOut (orkestratoren og research-agenten T, 28. september). Hvem artisten hører til
 * avgjøres av bookerens e-post:
 *
 *   * domenet er et byrå vi kjenner (en fils nettside, eller et registerbyrås nettside) →
 *     artisten er funnet hos DET byrået: @livenation.no → Live Nation, @act.as → Act Agency,
 *     @timeout.no → Timeout;
 *   * domenet er ukjent (@luger.no, @fkpscorpio.no) → artisten holdes tilbake til «Må sjekkes»:
 *     ingen Inn, Ut eller Byttet — og ingen «Ut» fra byrået hvis side hun står på;
 *   * ingen e-post → byrået fila er skrevet for.
 *
 * Agentene fordeles på samme måte, på sin egen e-post. En booker uten navn (jon@nordiclive.no)
 * blir en kontakt med adressen som navn.
 */
export function groupScanFiles(
  files: readonly { name: string; file: AgencyScanFile }[],
  knownAgencies: readonly { name: string; websiteUrl: string | null }[] = [],
): GroupedScanAgency[] {
  const groups = new Map<string, GroupedScanAgency>();
  const byDomain = new Map<string, string>();

  const ensure = (name: string): GroupedScanAgency => {
    const key = normalizeAgencyName(name);
    let group = groups.get(key);
    if (group === undefined) {
      group = {
        key,
        name: name.replace(/\s*\([^)]*\)\s*/g, ' ').trim(),
        website: null,
        status: 'ok',
        reason: null,
        sourceUrl: null,
        sources: [],
        files: [],
        ownFile: false,
        generalContact: null,
        agents: [],
        artists: [],
        held: [],
      };
      groups.set(key, group);
    }
    return group;
  };

  for (const known of knownAgencies) {
    const domain = registrableDomain(known.websiteUrl);
    if (domain !== null && !byDomain.has(domain)) byDomain.set(domain, known.name);
  }
  /* Filenes egne nettsider vinner over registeret: det er dem vi faktisk leste. */
  for (const { file } of files) {
    const domain = registrableDomain(file.website ?? null);
    if (domain !== null) byDomain.set(domain, file.agency);
  }
  const agencyOfDomain = (domain: string | null): string | null =>
    domain === null ? null : (byDomain.get(domain) ?? null);
  const hasAgent = (group: GroupedScanAgency, name: string): boolean =>
    group.agents.some((one) => normalizeAgentName(one.name) === normalizeAgentName(name));

  for (const { name: fileName, file } of files) {
    const home = ensure(file.agency);
    home.files.push(fileName);
    home.ownFile = true;
    home.website = clean(file.website) ?? home.website;
    home.sources.push(...(file.sources ?? []));
    home.sourceUrl = home.sourceUrl ?? file.sources?.[0] ?? clean(file.website);
    if (file.status !== undefined && file.status !== 'ok') {
      home.status = 'unreadable';
      home.reason = clean(file.reason) ?? file.status;
      continue;
    }

    const general = file.generalContact;
    if (general !== null && general !== undefined) {
      const email = clean(general.email);
      const phone = clean(general.phone);
      if (email !== null || phone !== null) {
        home.generalContact = { name: home.name, email, phone, source: clean(general.source) };
      }
    }

    const agentByEmail = new Map<string, ScanContact>();
    /** Agenten, og byrået hen hører til — null når hen jobber i et byrå vi ikke kjenner (Luger). */
    const agentByName = new Map<string, { contact: ScanContact; owner: string | null }>();
    for (const agent of file.agents ?? []) {
      const email = firstEmail(agent.email);
      const name = clean(agent.name) ?? email;
      if (name === null) continue;
      const contact: ScanContact = {
        name,
        email,
        phone: clean(agent.phone),
        source: clean(agent.source),
      };
      /*
       * Domenet først. Et ukjent domene er likevel fila sitt byrå (All Things Lives folk har
       * @stageway.no), MED MINDRE agenten selv sier et annet firma — da er hen ikke vår.
       */
      const company = clean(agent.company);
      const owner =
        agencyOfDomain(registrableDomain(email)) ??
        (company !== null && normalizeAgencyName(company) !== home.key ? null : file.agency);
      if (owner !== null) {
        const group = ensure(owner);
        if (!hasAgent(group, name)) group.agents.push(contact);
      }
      if (email !== null) agentByEmail.set(email, contact);
      agentByName.set(normalizeAgentName(name), { contact, owner });
    }

    for (const artist of file.artists ?? []) {
      const named = clean(artist.agent);
      const explicit = firstEmail(artist.agentEmail);
      const known = named === null ? undefined : agentByName.get(normalizeAgentName(named));
      const email = explicit ?? known?.contact.email ?? null;
      const agentName = named ?? (email === null ? null : (agentByEmail.get(email)?.name ?? email));
      /*
       * Bare en adresse SIDEN SKRIVER ved artisten flytter henne til et annet byrå. En adresse vi
       * slo opp via agentens navn følger agenten, og agenten er allerede fordelt over.
       */
      const domain =
        explicit !== null
          ? registrableDomain(explicit)
          : known === undefined || known.owner === file.agency
            ? null
            : registrableDomain(email);
      const scanned: ScannedArtist = {
        name: artist.name.trim(),
        agent: agentName,
        image: clean(artist.image),
        genre: clean(artist.genre),
        base: clean(artist.base),
        source: clean(artist.source),
        ...(artist.uncertain === true ? { uncertain: true } : {}),
      };
      if (domain === null) {
        home.artists.push(scanned);
        continue;
      }
      const owner = agencyOfDomain(domain);
      if (owner === null) {
        /*
         * ET UKJENT DOMENE ER BYRÅET SIDEN STÅR PÅ (Atle 29.09, «Må sjekkes» gruppe 3: «før opp
         * alle som peker til en annen adresse, vi godtar det»). Lugers artist på Live Nations
         * side er hos Live Nation, og bookeren er kontakten hennes der. Før ble hun holdt tilbake
         * som `unknown_agency` og kom tilbake under «Må sjekkes» i hver skanning.
         */
        home.artists.push(scanned);
        if (agentName !== null && email !== null && !hasAgent(home, agentName)) {
          home.agents.push(
            known?.contact ?? { name: agentName, email, phone: null, source: scanned.source },
          );
        }
        continue;
      }
      const group = ensure(owner);
      group.artists.push(scanned);
      /* En booker som bare finnes som adresse blir en kontakt med adressen som navn. */
      if (agentName !== null && email !== null && !hasAgent(group, agentName)) {
        group.agents.push({ name: agentName, email, phone: null, source: scanned.source });
      }
    }
  }

  return [...groups.values()];
}
